/**
 * syncedKeychain.ts — JS wrapper around the SyncedKeychainModule native
 * bridge.
 *
 * Behavior matrix
 * ---------------
 *   iOS, native module loaded:
 *     setItem / getItem / deleteItem hit the iCloud-synchronizable
 *     keychain. If the user has iCloud Keychain enabled (Settings →
 *     Apple ID → iCloud → Passwords and Keychain), values written here
 *     replicate to every device on the same Apple ID. If iCloud Keychain
 *     is disabled, values stay local; the user can still recover via the
 *     mnemonic flow on a new device.
 *
 *   iOS, native module not present (older app build, dev-client without
 *   the latest prebuild, or someone disabled the plugin):
 *     `isAvailable()` returns false. Callers MUST fall back to a non-
 *     syncing storage path (typically expo-secure-store) and a recovery
 *     flow on new devices.
 *
 *   Android:
 *     `isAvailable()` returns false. Block Store integration is a tracked
 *     follow-up (the cross-platform analogue), so for now Android users
 *     rely on the mnemonic recovery path.
 *
 * Threat model notes
 * ------------------
 * Items synced via this module are E2E-encrypted by Apple between
 * devices using the user's iCloud Security Code. Apple cannot read
 * synchronizable keychain items from server-side state. The threat
 * surface that this module DOES expand vs. local-only SecureStore is
 * "Apple Account compromise" — an attacker who fully takes over the
 * user's Apple ID and bypasses the iCloud security code prompt could
 * pull synced items off iCloud Keychain. Users who don't accept that
 * trust model should disable iCloud Keychain (or rotate their account
 * root key periodically — the mnemonic flow rotates by design).
 */

import { NativeModules, Platform } from 'react-native';

interface SyncedKeychainNative {
  setItem(key: string, value: string): Promise<boolean>;
  getItem(key: string): Promise<string | null>;
  deleteItem(key: string): Promise<boolean>;
  isAvailable(): Promise<boolean>;
}

const native: SyncedKeychainNative | undefined = (NativeModules as any).SyncedKeychainModule;

/**
 * True iff the native bridge is reachable on this platform/build.
 * Returns false (without throwing) on Android and on any iOS build that
 * predates the SyncedKeychainModule plugin. Caller decides whether to
 * fall back to a local-only storage path.
 */
export function isSyncedKeychainAvailable(): boolean {
  if (Platform.OS !== 'ios') return false;
  return Boolean(native && typeof native.setItem === 'function');
}

export async function setSyncedKeychainItem(key: string, value: string): Promise<boolean> {
  if (!isSyncedKeychainAvailable()) return false;
  try {
    return await native!.setItem(key, value);
  } catch {
    // Hard failure here is unusual but possible (e.g. keychain corruption,
    // device is in some weird locked state during First Unlock). Returning
    // false lets callers stash a local-only copy and try again later
    // without crashing the user's flow.
    return false;
  }
}

export async function getSyncedKeychainItem(key: string): Promise<string | null> {
  if (!isSyncedKeychainAvailable()) return null;
  try {
    const value = await native!.getItem(key);
    return value ?? null;
  } catch {
    return null;
  }
}

export async function deleteSyncedKeychainItem(key: string): Promise<boolean> {
  if (!isSyncedKeychainAvailable()) return false;
  try {
    return await native!.deleteItem(key);
  } catch {
    return false;
  }
}
